/* eslint-disable camelcase */
import { Box, Container, Stack, Typography } from '@mui/material';
import { memo } from 'react';
import CircleIcon from '@mui/icons-material/Circle';
import useInspection from 'hooks/useInspection';
import PositiveButton from 'components/cmn/PositiveButton';
import { inspectionLogger, getStepName } from 'utils/inspectionLogger';
import { useAppDispatch } from 'store';
import { postInspectionClickLogs } from 'store/inspection/inspectionSlice';
import { useProject } from 'hooks/useProject';

function ClassificationGuide2Part1(): JSX.Element {
  const dispatch = useAppDispatch();
  const { urlProjectId, urlPartCode } = useProject();
  const { classification, changeIsClassificationGuideStart2_1, inspectionDetail } = useInspection();
  
  const handleNext = () => {
    // 2단계 PART1 시작 버튼 클릭 로그 (비동기)
    if (urlProjectId && urlPartCode && inspectionDetail?.id) {
      inspectionLogger.logClick({
        projectId: urlProjectId,
        partCode: urlPartCode,
        inspectionId: inspectionDetail.id,
        step: '2_1' as const,
        stepName: getStepName('2_1'),
        actionType: 'next_button',
      });

      // 비동기로 전송 (백그라운드에서 실행)
      inspectionLogger.flush().then((pendingLogs) => {
        if (pendingLogs.length > 0) {
          dispatch(postInspectionClickLogs({ projectId: urlProjectId, logs: pendingLogs }))
            .unwrap()
            .then(() => {
              console.log(`✅ 2단계 PART1 시작 버튼 클릭 로그 ${pendingLogs.length}개 전송 완료`);
            })
            .catch((error) => {
              console.error('❌ 2단계 PART1 시작 버튼 클릭 로그 전송 실패:', error);
            });
        }
      });
    }

    changeIsClassificationGuideStart2_1(true);
  };
  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        width: '100%',
        justifyContent: 'center',
        alignItems: 'center',
        py: 4,
      }}
    >
      <Box
        sx={{
          p: 4,
          width: '100%',
          maxWidth: { xs: '100%', md: '1536px' },
          height: { xs: 'auto', md: '864px' },
        }}
      >
        <Container maxWidth="xl" sx={{ height: '100%' }}>
          <Stack
            direction="column"
            justifyContent="center"
            alignContent="center"
            alignItems="center"
            sx={{ height: '100%' }}
          >
            <Box
              sx={{
                mb: 3,
                fontSize: { xs: '19px', sm: '48px', md: '52px' },
                fontWeight: 900,
                textAlign: 'center',
              }}
            >
              {classification}단계 분류 PART 1 안내문
            </Box>

            <Container maxWidth="lg" sx={{ p: 0, height: 'calc(100% - 84px)' }}>
              <Typography
                sx={{
                  fontSize: { xs: '16px', sm: '18px', md: '20px' },
                  fontWeight: 400,
                  display: 'block',
                  lineHeight: { xs: '28px', sm: '32px', md: '35px' },
                  wordBreak: 'keep-all',
                }}
                variant="p"
                gutterBottom
              >
                2단계 PART 1을 시작합니다.
                <br />
                <Typography
                  sx={{
                    fontSize: { xs: '16px', sm: '18px', md: '20px' },
                    fontWeight: 400,
                    lineHeight: { xs: '28px', sm: '32px', md: '35px' },
                  }}
                >
                  <span>이 단계는&nbsp;</span>
                  <span style={{ color: '#0090FF', fontWeight: 700 }}>1단계에서 &nbsp;</span>
                  <span
                    style={{
                      color: '#0090FF',
                      fontWeight: 700,
                      textDecoration: 'underline',
                      wordBreak: 'keep-all',
                    }}
                  >
                    &apos;전적으로 동의함&apos;
                  </span>
                  <span style={{ color: '#0090FF', fontWeight: 700, wordBreak: 'keep-all' }}>
                    으로 선택한 문항들에 대해, 의견의 강도를 보다 세밀하게 측정하기 위한 과정
                  </span>
                  <span>입니다.</span>
                </Typography>
                <Typography
                  sx={{
                    fontSize: { xs: '16px', sm: '18px', md: '20px' },
                    fontWeight: 400,
                    lineHeight: { xs: '28px', sm: '32px', md: '35px' },
                    wordBreak: 'keep-all',
                  }}
                >
                  <span>1단계에서 특정 문항들을&nbsp;</span>
                  <span style={{ textDecoration: 'underline' }}>&apos;전적으로 동의함&apos;</span>
                  <span>으로 분류했지만, 이 문항들 사이에서도 상대적인 차이가 있을 수 있습니다.</span>
                </Typography>
                <Box sx={{ height: 10 }} />
                <Typography
                  sx={{
                    fontSize: { xs: '16px', sm: '18px', md: '20px' },
                    fontWeight: 400,
                    lineHeight: { xs: '28px', sm: '32px', md: '35px' },
                    wordBreak: 'keep-all',
                  }}
                >
                  <span>따라서,&nbsp;</span>
                  <span style={{ fontWeight: 700 }}>본 단계는&nbsp;</span>
                  <span
                    style={{
                      fontWeight: 700,
                      textDecoration: 'underline',
                    }}
                  >
                    &apos;전적으로 동의함&apos;
                  </span>
                  <span style={{ fontWeight: 700 }}>
                    으로 선택한 문항들을 다시 비교하여, 그 강도를 상대적으로 분류하는 과정
                  </span>
                  <span>입니다.</span>
                </Typography>
                <Box sx={{ height: 20 }} />
                <Typography
                  sx={{
                    fontSize: { xs: '16px', sm: '18px', md: '20px' },
                    fontWeight: 700,
                    lineHeight: { xs: '28px', sm: '32px', md: '35px' },
                  }}
                >
                  응답 방식 안내:
                </Typography>
                <Box sx={{ height: 10 }} />
                <Box>
                  <Typography
                    sx={{
                      fontSize: { xs: '16px', sm: '18px', md: '20px' },
                      fontWeight: 400,
                      lineHeight: { xs: '28px', sm: '32px', md: '35px' },
                      wordBreak: 'keep-all',
                    }}
                  >
                    <CircleIcon sx={{ fontSize: '6px', mx: '8px' }} />
                    <span>1단계에서&nbsp;</span>
                    <span style={{ textDecoration: 'underline' }}>&apos;전적으로 동의함&apos;</span>
                    <span>으로 선택한 여러 문항들 중에서 자신의 신념 강도를 상대적으로 비교하여 응답합니다.</span>
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: { xs: '16px', sm: '18px', md: '20px' },
                      fontWeight: 400,
                      lineHeight: { xs: '28px', sm: '32px', md: '35px' },
                      wordBreak: 'keep-all',
                    }}
                  >
                    <CircleIcon sx={{ fontSize: '6px', mx: '8px' }} />
                    <span>예를 들어, 4개의 문항이 &apos;전적으로 동의함&apos;으로 선택되었다면,</span>
                  </Typography>

                  <Typography
                    sx={{
                      fontSize: { xs: '16px', sm: '18px', md: '20px' },
                      fontWeight: 400,
                      lineHeight: { xs: '28px', sm: '32px', md: '35px' },
                      wordBreak: 'keep-all',
                    }}
                  >
                    <CircleIcon sx={{ fontSize: '6px', ml: '32px', mr: '8px' }} />
                    <span>가장 강하게 동의하는 문항을&nbsp;</span>
                    <span style={{ fontWeight: 700 }}>&apos;전적으로 동의함&apos;</span>
                    <span>으로</span>
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: { xs: '16px', sm: '18px', md: '20px' },
                      fontWeight: 400,
                      lineHeight: { xs: '28px', sm: '32px', md: '35px' },
                      wordBreak: 'keep-all',
                    }}
                  >
                    <CircleIcon sx={{ fontSize: '6px', ml: '32px', mr: '8px' }} />
                    <span>그보다 조금 낮은 신념의 문항을&nbsp;</span>
                    <span style={{ fontWeight: 700 }}>&apos;동의함&apos;</span>
                    <span>으로</span>
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: { xs: '16px', sm: '18px', md: '20px' },
                      fontWeight: 400,
                      lineHeight: { xs: '28px', sm: '32px', md: '35px' },
                      wordBreak: 'keep-all',
                    }}
                  >
                    <CircleIcon sx={{ fontSize: '6px', ml: '32px', mr: '8px' }} />
                    <span>약간 동의하지만 일부 이견이 있는 문항을&nbsp;</span>
                    <span style={{ fontWeight: 700 }}>&apos;동의하지 않음&apos;</span>
                    <span>으로</span>
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: { xs: '16px', sm: '18px', md: '20px' },
                      fontWeight: 400,
                      lineHeight: { xs: '28px', sm: '32px', md: '35px' },
                      wordBreak: 'keep-all',
                    }}
                  >
                    <CircleIcon sx={{ fontSize: '6px', ml: '32px', mr: '8px' }} />
                    <span>상대적으로 가장 약한 신념의 문항을&nbsp;</span>
                    <span style={{ fontWeight: 700 }}>&apos;전혀 동의하지 않음&apos;</span>
                    <span>으로 다시 구분하게 됩니다.</span>
                  </Typography>
                </Box>
                <Box sx={{ height: 20 }} />
                <Box>
                  <Typography
                    sx={{
                      fontSize: { xs: '16px', sm: '18px', md: '20px' },
                      fontWeight: 700,
                      lineHeight: { xs: '28px', sm: '32px', md: '35px' },
                    }}
                  >
                    유의사항:
                  </Typography>
                  <Box sx={{ height: 10 }} />
                  <Typography
                    sx={{
                      fontSize: { xs: '16px', sm: '18px', md: '20px' },
                      fontWeight: 400,
                      lineHeight: { xs: '28px', sm: '32px', md: '35px' },
                      wordBreak: 'keep-all',
                    }}
                  >
                    <CircleIcon sx={{ fontSize: '6px', mx: '8px' }} />
                    <span>이 과정은&nbsp;</span>
                    <span style={{ color: '#FF0000', fontWeight: 700 }}>
                      자신의 의견을 수정하는 것이 아니라, 이미 전적으로 동의했던 문항들 중에서 신념의 강도를 더 정확하게
                      파악하기 위한 과정
                    </span>
                    <span>입니다.</span>
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: { xs: '16px', sm: '18px', md: '20px' },
                      fontWeight: 400,
                      lineHeight: { xs: '28px', sm: '32px', md: '35px' },
                      wordBreak: 'keep-all',
                    }}
                  >
                    <CircleIcon sx={{ fontSize: '6px', mx: '8px' }} />
                    <span style={{ fontWeight: 700 }}>
                      응답 방식은 앞선 단계와 동일하며, 각 문항을 신중하게 비교하여 응답해 주세요
                    </span>
                  </Typography>
                </Box>
                <Box sx={{ height: 10 }} />
              </Typography>
            </Container>
          </Stack>
        </Container>
      </Box>
      {/* 하단 버튼 영역 높이만큼 여백 추가 (버튼 높이 52px + 패딩 32px = 약 84px) */}
      <Box sx={{ height: { xs: 84, sm: 84 } }} />
      <Box
        sx={{
          position: 'fixed',
          bottom: 0,
          left: 0,
          width: '100%',
          bgcolor: 'background.paper',
          boxShadow: '0px -2px 10px rgba(0, 0, 0, 0.1)',
          py: 2,
          display: 'flex',
          justifyContent: 'center',
        }}
      >
        <PositiveButton
          variant="contained"
          onClick={handleNext}
          sx={{ width: { xs: 130 }, height: 52, typography: 's14w500' }}
          title="시작"
        />
      </Box>
    </Box>
  );
}

export default memo(ClassificationGuide2Part1);
